import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'

const TermsAndConditions = () => {
  const navigate = useNavigate()

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const currentDate = new Date().toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 lg:relative">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors mb-4"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm sm:text-base font-medium">Volver</span>
          </button>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">
            Términos y Condiciones
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">
            Última actualización: {currentDate}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 lg:p-12">
          <div className="prose prose-sm sm:prose-base lg:prose-lg max-w-none">
            
            {/* Introducción */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                1. Introducción
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Bienvenido a <strong>ColmaFácil</strong>, un marketplace digital que conecta clientes con colmados 
                en República Dominicana. Al acceder y utilizar nuestra plataforma, usted acepta cumplir con estos 
                Términos y Condiciones. Si no está de acuerdo con alguna parte de estos términos, no debe utilizar 
                nuestros servicios.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Estos términos constituyen un acuerdo legalmente vinculante entre usted y ColmaFácil. Le recomendamos 
                leer cuidadosamente este documento antes de utilizar nuestros servicios.
              </p>
            </section>

            {/* Aceptación de Términos */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                2. Aceptación de los Términos
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Al registrarse, acceder o utilizar cualquier parte de la plataforma ColmaFácil, usted confirma que:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Tiene al menos 18 años de edad o cuenta con el consentimiento de un tutor legal</li>
                <li>Tiene la capacidad legal para celebrar contratos vinculantes</li>
                <li>Proporcionará información veraz, precisa y completa</li>
                <li>Mantendrá la confidencialidad de su cuenta y contraseña</li>
                <li>Acepta estos Términos y Condiciones en su totalidad</li>
              </ul>
            </section>

            {/* Descripción del Servicio */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                3. Descripción del Servicio
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                ColmaFácil es una plataforma digital que facilita la conexión entre:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li><strong>Clientes:</strong> Personas que buscan productos de colmados y desean realizar compras con entrega a domicilio</li>
                <li><strong>Colmados:</strong> Establecimientos comerciales registrados que ofrecen productos a través de la plataforma</li>
                <li><strong>Empleados:</strong> Personal autorizado de los colmados para gestionar pedidos y entregas</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                ColmaFácil actúa únicamente como intermediario. No somos propietarios de los productos, no almacenamos 
                inventario, ni somos responsables de la calidad, disponibilidad o entrega de los productos ofrecidos 
                por los colmados registrados.
              </p>
            </section>

            {/* Registro y Cuentas de Usuario */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                4. Registro y Cuentas de Usuario
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                4.1. Creación de Cuenta
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Para utilizar nuestros servicios, debe crear una cuenta proporcionando información precisa y actualizada. 
                Usted es responsable de mantener la confidencialidad de sus credenciales de acceso y de todas las 
                actividades que ocurran bajo su cuenta.
              </p>
              
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                4.2. Registro de Colmados
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Los colmados que deseen registrarse deben:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Proporcionar información comercial veraz y completa</li>
                <li>Contar con los permisos y licencias necesarias para operar en República Dominicana</li>
                <li>Someterse al proceso de aprobación de ColmaFácil</li>
                <li>Mantener información actualizada sobre productos, precios y disponibilidad</li>
                <li>Cumplir con todas las obligaciones fiscales y legales aplicables</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                4.3. Seguridad de la Cuenta
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Usted es responsable de notificarnos inmediatamente sobre cualquier uso no autorizado de su cuenta. 
                ColmaFácil no será responsable de pérdidas o daños resultantes del uso no autorizado de su cuenta.
              </p>
            </section>

            {/* Uso de la Plataforma */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                5. Uso de la Plataforma
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                5.1. Uso Permitido
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Usted puede utilizar nuestra plataforma únicamente para fines legales y de acuerdo con estos términos. 
                Está permitido:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Navegar y buscar productos y colmados</li>
                <li>Realizar compras legítimas</li>
                <li>Gestionar su cuenta y pedidos</li>
                <li>Comunicarse con colmados a través de los canales oficiales de la plataforma</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                5.2. Uso Prohibido
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Está estrictamente prohibido:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Utilizar la plataforma para actividades ilegales o fraudulentas</li>
                <li>Intentar acceder a áreas restringidas o sistemas de la plataforma</li>
                <li>Interferir con el funcionamiento normal de la plataforma</li>
                <li>Copiar, modificar o distribuir contenido sin autorización</li>
                <li>Realizar ingeniería inversa o intentar extraer código fuente</li>
                <li>Utilizar bots, scripts automatizados o cualquier método para manipular la plataforma</li>
                <li>Suplantar la identidad de otra persona o entidad</li>
                <li>Publicar contenido ofensivo, difamatorio o que viole derechos de terceros</li>
              </ul>
            </section>

            {/* Pedidos y Transacciones */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                6. Pedidos y Transacciones
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                6.1. Realización de Pedidos
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Al realizar un pedido a través de ColmaFácil:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Usted hace una oferta de compra al colmado seleccionado</li>
                <li>El colmado tiene el derecho de aceptar o rechazar su pedido</li>
                <li>El pedido solo se considera confirmado cuando el colmado lo acepta</li>
                <li>Los precios mostrados son establecidos por cada colmado y pueden variar</li>
                <li>Los productos están sujetos a disponibilidad</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                6.2. Precios y Pagos
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Los precios de los productos son establecidos por cada colmado y pueden incluir:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Precio del producto</li>
                <li>Costo de delivery (si aplica)</li>
                <li>Impuestos aplicables según la legislación dominicana</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Los métodos de pago aceptados son determinados por cada colmado. ColmaFácil no procesa pagos 
                directamente, pero puede facilitar la integración con procesadores de pago de terceros.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                6.3. Entrega
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                La entrega de productos es responsabilidad del colmado. Los tiempos de entrega son estimados y 
                pueden variar según:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Disponibilidad del producto</li>
                <li>Distancia y ubicación de entrega</li>
                <li>Condiciones climáticas o de tráfico</li>
                <li>Capacidad operativa del colmado</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                ColmaFácil no garantiza tiempos de entrega específicos y no es responsable por retrasos en la 
                entrega que estén fuera de nuestro control.
              </p>
            </section>

            {/* Política de Cancelación y Reembolsos */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                7. Cancelación y Reembolsos
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                7.1. Cancelación por el Cliente
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Los clientes pueden cancelar pedidos antes de que sean aceptados por el colmado. Una vez aceptado 
                el pedido, la cancelación está sujeta a la política del colmado correspondiente.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                7.2. Cancelación por el Colmado
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Los colmados pueden cancelar pedidos en caso de:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Falta de disponibilidad del producto</li>
                <li>Problemas de pago</li>
                <li>Dirección de entrega no válida o inaccesible</li>
                <li>Circunstancias excepcionales que impidan la preparación o entrega</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                7.3. Reembolsos
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Las políticas de reembolso son determinadas por cada colmado. ColmaFácil facilitará la comunicación 
                entre cliente y colmado para resolver disputas, pero no es responsable de procesar reembolsos 
                directamente, salvo que sea requerido por ley.
              </p>
            </section>

            {/* Responsabilidades y Limitaciones */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                8. Responsabilidades y Limitaciones
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                8.1. Responsabilidad de ColmaFácil
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                ColmaFácil actúa como intermediario tecnológico. Nuestra responsabilidad se limita a:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Proporcionar una plataforma funcional y segura</li>
                <li>Facilitar la comunicación entre clientes y colmados</li>
                <li>Procesar información de manera segura según nuestra Política de Privacidad</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                8.2. Limitación de Responsabilidad
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                ColmaFácil NO será responsable de:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Calidad, seguridad o autenticidad de los productos ofrecidos por los colmados</li>
                <li>Retrasos, pérdidas o daños en la entrega de productos</li>
                <li>Disputas entre clientes y colmados</li>
                <li>Errores en precios, disponibilidad o descripciones de productos</li>
                <li>Daños indirectos, incidentales o consecuentes</li>
                <li>Pérdida de datos, ingresos o beneficios</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                En ningún caso la responsabilidad total de ColmaFácil excederá el monto de las comisiones 
                recibidas en los últimos 12 meses relacionados con su uso de la plataforma.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                8.3. Responsabilidad del Colmado
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Cada colmado es responsable de:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>La calidad y seguridad de sus productos</li>
                <li>Cumplir con todas las leyes y regulaciones aplicables</li>
                <li>Mantener información precisa sobre productos y precios</li>
                <li>Procesar y entregar pedidos de manera oportuna</li>
                <li>Gestionar reclamaciones y reembolsos según su política</li>
              </ul>
            </section>

            {/* Propiedad Intelectual */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                9. Propiedad Intelectual
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Todos los derechos de propiedad intelectual relacionados con la plataforma ColmaFácil, incluyendo 
                pero no limitado a:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Diseño, logotipos, marcas y nombres comerciales</li>
                <li>Código fuente, software y tecnología</li>
                <li>Contenido, textos, imágenes y materiales</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mb-4">
                Son propiedad exclusiva de ColmaFácil o sus licenciantes y están protegidos por las leyes de 
                propiedad intelectual de República Dominicana y tratados internacionales.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Usted no puede copiar, modificar, distribuir, vender o crear obras derivadas basadas en nuestra 
                plataforma sin autorización escrita previa.
              </p>
            </section>

            {/* Privacidad y Protección de Datos */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                10. Privacidad y Protección de Datos
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                El tratamiento de sus datos personales se rige por nuestra <strong>Política de Privacidad</strong>, 
                que forma parte integral de estos términos. Al utilizar nuestros servicios, usted consiente el 
                tratamiento de sus datos personales según lo descrito en dicha política.
              </p>
              <p className="text-gray-700 leading-relaxed">
                ColmaFácil cumple con la Ley No. 172-13 sobre Protección de Datos Personales de República Dominicana 
                y se compromete a proteger su privacidad y datos personales.
              </p>
            </section>

            {/* Modificaciones de los Términos */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                11. Modificaciones de los Términos
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                ColmaFácil se reserva el derecho de modificar estos Términos y Condiciones en cualquier momento. 
                Las modificaciones entrarán en vigor inmediatamente después de su publicación en la plataforma.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                Le notificaremos sobre cambios significativos mediante:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Notificación en la plataforma</li>
                <li>Correo electrónico a la dirección registrada</li>
                <li>Actualización de la fecha de "Última actualización"</li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                El uso continuado de la plataforma después de las modificaciones constituye su aceptación de los 
                nuevos términos. Si no está de acuerdo con las modificaciones, debe dejar de utilizar nuestros 
                servicios y puede solicitar la eliminación de su cuenta.
              </p>
            </section>

            {/* Terminación */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                12. Terminación
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                12.1. Terminación por el Usuario
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Usted puede cerrar su cuenta en cualquier momento contactándonos a través de los canales oficiales 
                de soporte.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                12.2. Terminación por ColmaFácil
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                ColmaFácil puede suspender o terminar su cuenta inmediatamente si:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Viola estos Términos y Condiciones</li>
                <li>Realiza actividades fraudulentas o ilegales</li>
                <li>Proporciona información falsa o engañosa</li>
                <li>Interfiere con el funcionamiento de la plataforma</li>
                <li>No utiliza la cuenta por un período prolongado</li>
              </ul>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                12.3. Efectos de la Terminación
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Al terminar su cuenta, perderá acceso a todos los servicios y datos asociados, excepto cuando 
                la ley requiera que mantengamos cierta información. Los pedidos en curso se procesarán según 
                corresponda, pero no podrá realizar nuevos pedidos.
              </p>
            </section>

            {/* Ley Aplicable y Jurisdicción */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                13. Ley Aplicable y Jurisdicción
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Estos Términos y Condiciones se rigen por las leyes de la República Dominicana. Cualquier 
                disputa relacionada con estos términos o con el uso de la plataforma será resuelta en los 
                tribunales competentes de República Dominicana.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Las partes se comprometen a intentar resolver cualquier disputa mediante negociación de buena fe 
                antes de recurrir a procedimientos legales formales.
              </p>
            </section>

            {/* Disposiciones Generales */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                14. Disposiciones Generales
              </h2>
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                14.1. Integridad del Acuerdo
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Estos términos, junto con nuestra Política de Privacidad, constituyen el acuerdo completo entre 
                usted y ColmaFácil respecto al uso de la plataforma.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                14.2. Divisibilidad
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Si alguna disposición de estos términos se considera inválida o inaplicable, las demás disposiciones 
                permanecerán en pleno vigor y efecto.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                14.3. Renuncia
              </h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                El hecho de que ColmaFácil no ejerza algún derecho bajo estos términos no constituye una renuncia 
                a dicho derecho.
              </p>

              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                14.4. Cesión
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Usted no puede ceder o transferir sus derechos u obligaciones bajo estos términos sin el 
                consentimiento previo por escrito de ColmaFácil. ColmaFácil puede ceder estos términos sin 
                restricción.
              </p>
            </section>

            {/* Contacto */}
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4">
                15. Contacto
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Si tiene preguntas, comentarios o inquietudes sobre estos Términos y Condiciones, puede 
                contactarnos a través de:
              </p>
              <ul className="list-none space-y-2 text-gray-700">
                <li className="flex items-start">
                  <strong className="mr-2">Email:</strong>
                  <a href="mailto:info@colmafacil.store" className="text-primary-600 hover:text-primary-700">
                    colmafacil@gmail.com
                  </a>
                </li>
                <li className="flex items-start">
                  <strong className="mr-2">Teléfono:</strong>
                  <a href="tel:+18294419998" className="text-primary-600 hover:text-primary-700">
                    +1 (829) 441-9998
                  </a>
                </li>
                <li className="flex items-start">
                  <strong className="mr-2">Ubicación:</strong>
                  <span>República Dominicana</span>
                </li>
              </ul>
            </section>

            {/* Aceptación */}
            <section className="mt-12 pt-8 border-t border-gray-200">
              <p className="text-gray-700 leading-relaxed text-center">
                <strong>Al utilizar ColmaFácil, usted reconoce haber leído, entendido y aceptado estos 
                Términos y Condiciones en su totalidad.</strong>
              </p>
            </section>

          </div>
        </div>
      </div>
    </div>
  )
}

export default TermsAndConditions

